import { useEffect, useRef, useState } from "react";
import SecureStreamPlayer, {
  type StreamPlayerHandle,
} from "../streaming/StreamPlayer";
import api from "../../lib/api";

interface Props {
  isLive: boolean;
  isPaused?: boolean;
  showResumedBanner?: boolean;
  isRecording?: boolean;
  streamPath: string;
  user: string;
  ip: string;
  caseId: string;
  courtRoomLabel?: string;
  courtName: string;
  mobile?: string;
  location?: string;
  cnr?: string;
}

export default function PublicViewerPlayer({
  isLive,
  isPaused,
  showResumedBanner,
  streamPath,
  user,
  isRecording,
  ip,
  cnr,
  courtRoomLabel,
  courtName,
  mobile,
  location,
}: Props) {
  const playerRef = useRef<StreamPlayerHandle>(null);
  const [hlsToken, setHlsToken] = useState<string>("");

  // imperative pause/play instead of unmount — mirrors UniversalPlayer
  useEffect(() => {
    if (!playerRef.current) return;
    if (isPaused) {
      playerRef.current.pause();
    } else if (isLive) {
      playerRef.current.play();
    }
  }, [isPaused, isLive]);

  useEffect(() => {
    console.log("[HLS Token] useEffect mounted");

    const fetchToken = () => {
      console.log("[HLS Token] Fetching token...");
      console.log("[HLS Token] API URL:", "/api/search/stream/hls-token");

      api
        .get("/api/search/stream/public-hls-token")
        .then((res) => {
          console.log("[HLS Token] API response:", res);
          console.log("[HLS Token] Token received:", res.data?.token);

          setHlsToken(res.data.token);
        })
        .catch((error) => {
          console.error("[HLS Token] API error:", error);
          console.error("[HLS Token] Error response:", error?.response);
          console.error("[HLS Token] Error status:", error?.response?.status);
          console.error("[HLS Token] Error data:", error?.response?.data);
        });
    };

    console.log("[HLS Token] Initial fetch");
    fetchToken();

    console.log("[HLS Token] Setting refresh interval: 30 minutes");

    const interval = setInterval(
      () => {
        console.log("[HLS Token] 30-minute interval triggered");
        fetchToken();
      },
      30 * 60 * 1000,
    );

    return () => {
      console.log("[HLS Token] useEffect cleanup - clearing interval");
      clearInterval(interval);
    };
  }, []);

  // force play on a new source (switch), status can stay "live" across it
  useEffect(() => {
    if (!streamPath || !isLive || isPaused) return;
    const t = setTimeout(() => playerRef.current?.play(), 300);
    return () => clearTimeout(t);
  }, [streamPath, isLive, isPaused]);

  const overlay = () => {
    if (isRecording && !isLive) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center bg-black gap-3 px-4 text-center">
          <div className="w-3 h-3 rounded-full bg-violet-400 animate-pulse" />
          <span className="text-white text-lg sm:text-2xl font-medium">
            Only recording has started.
          </span>
          <span className="text-gray-400 text-sm sm:text-base">
            {courtRoomLabel || "This courtroom"} is not live streaming right
            now.
          </span>
        </div>
      );
    }
    if (isPaused) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center bg-black gap-3 px-4 text-center">
          <div className="flex gap-2 mb-2">
            <span className="block w-3 h-10 bg-white rounded-sm opacity-90" />
            <span className="block w-3 h-10 bg-white rounded-sm opacity-90" />
          </div>
          <p className="text-white text-xl sm:text-2xl font-semibold">
            Streaming paused by Judge.
          </p>
          <p className="text-gray-400 text-sm">
            {courtRoomLabel || "This courtroom"} — please remain connected
          </p>
        </div>
      );
    }
    if (!isLive || !streamPath) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-black px-4">
          <div className="text-center">
            <div className="text-gray-400 text-base sm:text-lg md:text-xl mb-2">
              {courtRoomLabel || "This courtroom"}
            </div>
            <div className="text-white font-semibold text-lg sm:text-2xl md:text-3xl lg:text-4xl leading-snug">
              No live courtroom available.
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const currentOverlay = overlay();

  return (
    <div className="w-full h-full flex flex-col bg-black relative">
      <div className="relative flex-1 min-h-0 bg-black flex items-center justify-center overflow-hidden">
        {showResumedBanner && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[300] bg-green-600 text-white text-sm sm:text-base font-semibold px-4 py-2 rounded-full shadow-lg">
            Streaming has been resumed
          </div>
        )}

        {/* player always mounted — key stable so it never remounts */}
        <div className="w-full h-full">
          <style>{`
            video {
              width: 100% !important;
              height: 99% !important;
              object-fit: contain !important;
              object-position: center center !important;
            }
          `}</style>
          <SecureStreamPlayer
            key="stable-player"
            ref={playerRef}
            streamPath={streamPath}
            hlsToken={hlsToken}
            user={user}
            ip={ip}
            caseId={cnr || "N/A"}
            courtName={courtName}
            mobile={mobile ?? ""}
            location={location}
            isPaused={isPaused}
            enableFullscreen={false}
          />
        </div>

        {currentOverlay && (
          <div className="absolute inset-0 z-20 bg-black">{currentOverlay}</div>
        )}
      </div>
    </div>
  );
}
